/**
 * Composes pinned-messages, scroll-to-pinned, and header action hooks.
 * Extracted from useChatViewInit to keep that hook's body under the
 * complexity ceiling and to isolate pin-related wiring.
 */

import type { ChatItem } from '@/components/chat/chat-message.interface'
import type { UseChatVirtualListReturn } from '@/components/chat/use-chat-virtual-list.hook'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

import type { UseChatHeaderActionsReturn } from './use-chat-header-actions.hook'
import { useChatHeaderActions } from './use-chat-header-actions.hook'
import { useChatScrollToMessage } from './use-chat-scroll-to-message.hook'
import { usePinnedMessages } from './use-pinned-messages.hook'
import type { UseScrollToMessageReturn } from './use-scroll-to-message.hook'

interface UseChatPinningSetupParams {
  conversationId: string | null
  virtualListBinding: UseChatVirtualListReturn
  itemList: ChatItem[]
  userLookup: UserLookupMap
  onInvite?: () => void
  onLeave?: () => void
}

interface UseChatPinningSetupReturn {
  pinnedMessageList: ReturnType<typeof usePinnedMessages>['pinnedMessageList']
  headerActions: UseChatHeaderActionsReturn
  scroll: UseScrollToMessageReturn
}

export const useChatPinningSetup = (
  params: UseChatPinningSetupParams
): UseChatPinningSetupReturn => {
  const pinnedMessages = usePinnedMessages({
    conversationId: params.conversationId,
    userLookup: params.userLookup,
  })

  const scroll = useChatScrollToMessage({
    conversationId: params.conversationId,
    virtuosoRef: params.virtualListBinding.virtuosoRef,
    firstItemIndex: params.virtualListBinding.firstItemIndex,
    itemList: params.itemList,
    userLookup: params.userLookup,
  })

  const headerActions = useChatHeaderActions({
    conversationId: params.conversationId,
    itemList: params.itemList,
    pinnedMessageList: pinnedMessages.pinnedMessageList,
    isItemPinned: pinnedMessages.isItemPinned,
    onPinnedMessageSelected: (pin) =>
      scroll.scrollToMessage({
        id: pin.id,
        pageNumber: pin.pageNumber,
        pageSize: pin.pageSize,
      }),
    onInvite: params.onInvite,
    onLeave: params.onLeave,
  })

  return {
    pinnedMessageList: pinnedMessages.pinnedMessageList,
    headerActions,
    scroll,
  }
}
